import { hashToHex } from 'js/lib/hash';
import type { Location } from 'js/lib/useRouter';

import { captureMessageInSentry } from 'bundles/ai-coach-platform/utils/sentry';
import localStorageEx from 'bundles/common/utils/localStorageEx';
import sessionStoreEx from 'bundles/common/utils/sessionStorageEx';

import {
  ACTIVE_COURSERA_COACH_CONTINUOUS_SESSION_STORAGE_KEY,
  ACTIVE_COURSERA_COACH_SESSION_STORAGE_KEY,
} from '../constants';

export function getHashKeyForString(promptText?: string): string {
  // it is expected we will get a string, but incase of failure, we will return a timestamp
  // providing a uniqe value here will ensure that we do not collide with chat session storage keys
  // the probability of creating a collision with timestamps is extremely low, but not impossible. this is a fallback in case of failure.
  if (!promptText) {
    return `${Date.now()}`;
  }
  try {
    return hashToHex(promptText);
  } catch {
    return `${Date.now()}`;
  }
}

export function createChatSessionStorageKey(uniqueDataString?: string) {
  return `${ACTIVE_COURSERA_COACH_SESSION_STORAGE_KEY}-${getHashKeyForString(uniqueDataString)}`;
}

export function getChatSessionStorageKey(location: Location, uniqueDataString?: string) {
  const locationString = location.pathname + location.search + location.query;
  const sessionStorageKeyData = uniqueDataString || locationString;

  return createChatSessionStorageKey(sessionStorageKeyData);
}

export const persistentChatSessionId = {
  get: (key: string) => {
    const chatSessionIdFromMemory = sessionStoreEx.getItem<string | undefined>(key, (value) => value, undefined);

    if (!chatSessionIdFromMemory) {
      // handle invalid sessionId that may have been incorrectly set previously
      if (chatSessionIdFromMemory === '') {
        captureMessageInSentry({
          error: new Error('Found empty string sessionId'),
          source: 'persistentChatSessionId.get',
          extra: {
            chatSessionStorageKey: key,
          },
          level: 'info',
        });

        // remove the invalid sessionId from memory
        sessionStoreEx.removeItem(key);
      }

      return undefined;
    }

    const sanitizedChatSessionId = chatSessionIdFromMemory !== 'undefined' ? chatSessionIdFromMemory : undefined;
    return sanitizedChatSessionId;
  },
  set: (key: string, sessionId: string) => {
    const chatSessionId = sessionId;
    if (chatSessionId === '') {
      // ignore setting empty string sessionId to memory
      captureMessageInSentry({
        error: new Error('Found empty string sessionId'),
        source: 'persistentChatSessionId.set',
        extra: { chatSessionStorageKey: key },
        level: 'info',
      });
      return;
    }

    sessionStoreEx.setItem(key, chatSessionId, (value) => value);
  },
  clear: (key: string) => {
    sessionStoreEx.removeItem(key);
  },
};

export function createContinuousChatSessionStorageKey(uniqueDataString: string) {
  return `${ACTIVE_COURSERA_COACH_CONTINUOUS_SESSION_STORAGE_KEY}-${uniqueDataString}`;
}

export function getContinuousChatSessionStorageKey(location: Location, courseId?: string, uniqueDataString?: string) {
  // If courseId is undefined, we use the courseSlug as the key instead. We get this from the location pathname e.g./learn/course-slug/home/module/1
  const pathSegments = location.pathname.split('/');
  const courseSlug = pathSegments[2];
  const sessionStorageKeyData = uniqueDataString || courseId || courseSlug;

  // @ts-expect-error TS2345 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
  return createContinuousChatSessionStorageKey(sessionStorageKeyData);
}

export const persistentContinuousChatSessionId = {
  get: (key: string) => {
    const chatSessionIdFromMemory = localStorageEx.getItem<string | undefined>(key, (value) => value, undefined);

    if (!chatSessionIdFromMemory) {
      // handle invalid sessionId that may have been incorrectly set previously
      if (chatSessionIdFromMemory === '') {
        captureMessageInSentry({
          error: new Error('Found empty string sessionId'),
          source: 'persistentContinuousChatSessionId.get',
          extra: {
            chatSessionStorageKey: key,
          },
          level: 'info',
        });

        // remove the invalid sessionId from memory
        localStorageEx.removeItem(key);
      }

      return undefined;
    }

    const sanitizedChatSessionId = chatSessionIdFromMemory !== 'undefined' ? chatSessionIdFromMemory : undefined;
    return sanitizedChatSessionId;
  },
  set: (key: string, sessionId: string) => {
    const chatSessionId = sessionId;

    if (chatSessionId === '') {
      // ignore setting empty string sessionId to localStorage
      captureMessageInSentry({
        error: new Error('Found empty string sessionId'),
        source: 'persistentContinuousChatSessionId.set',
        extra: { chatSessionStorageKey: key },
        level: 'info',
      });
      return;
    }

    localStorageEx.setItem(key, chatSessionId, (value) => value);
  },
  clear: (key: string) => {
    localStorageEx.removeItem(key);
  },
};

export const clearAllChatSessionKeys = () => {
  sessionStoreEx.clearAllKeysWithPrefix(ACTIVE_COURSERA_COACH_SESSION_STORAGE_KEY);
};

export const clearAllContinuousChatSessionKeys = () => {
  localStorageEx.clearAllKeysWithPrefix(ACTIVE_COURSERA_COACH_CONTINUOUS_SESSION_STORAGE_KEY);
};
